import React from 'react';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';

export interface TutorPublicVisibilityToggleProps {
  visible: boolean;
  onChange: (visible: boolean) => void;
  disabled?: boolean;
  label: string;
  onHint: string;
  offHint: string;
  /** Right-align label copy — fits page headers; left for cards. */
  textAlign?: 'left' | 'right';
  /** `panel` shows label + hint; `icon` is icon-only with tooltip (card headers). */
  variant?: 'panel' | 'icon';
  sx?: SxProps<Theme>;
}

const getPanelSx = (theme: Theme) => ({
  borderRadius: 2,
  border: 1,
  borderColor: 'divider',
  bgcolor: alpha(theme.palette.background.paper, 0.55),
  px: 1.5,
  py: 1.1,
});

const getIconButtonSx = (theme: Theme, visible: boolean) => ({
  width: 38,
  height: 38,
  borderRadius: 1.25,
  flexShrink: 0,
  bgcolor: visible ? alpha(theme.palette.primary.main, 0.16) : alpha(theme.palette.text.disabled, 0.1),
  color: visible ? 'primary.light' : 'text.disabled',
  border: 1,
  borderColor: visible ? alpha(theme.palette.primary.main, 0.38) : theme.palette.divider,
  transition: 'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.15s ease',
  '&:hover': {
    bgcolor: visible ? alpha(theme.palette.primary.main, 0.24) : alpha(theme.palette.text.disabled, 0.16),
    borderColor: visible ? alpha(theme.palette.primary.main, 0.55) : theme.palette.divider,
  },
  '&:active': {
    transform: 'scale(0.96)',
  },
  '&.Mui-disabled': {
    opacity: 0.55,
    color: visible ? 'primary.light' : 'text.disabled',
  },
});

const TutorPublicVisibilityToggle: React.FC<TutorPublicVisibilityToggleProps> = ({
  visible,
  onChange,
  disabled = false,
  label,
  onHint,
  offHint,
  textAlign = 'left',
  variant = 'panel',
  sx,
}) => {
  const hint = visible ? onHint : offHint;

  const iconButton = (
    <IconButton
      size="small"
      disabled={disabled}
      onClick={() => onChange(!visible)}
      aria-label={label}
      aria-pressed={visible}
      sx={(theme) => getIconButtonSx(theme, visible)}
    >
      {visible ? (
        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
      ) : (
        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
      )}
    </IconButton>
  );

  if (variant === 'icon') {
    return (
      <Box sx={sx} onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        <Tooltip title={hint} describeChild>
          <span>{iconButton}</span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={[(theme) => getPanelSx(theme), ...(sx ? (Array.isArray(sx) ? sx : [sx]) : [])]}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box sx={{ minWidth: 0, flex: 1, textAlign }}>
          <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.35 }}>
            {label}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2, lineHeight: 1.45 }}>
            {hint}
          </Typography>
        </Box>
        {iconButton}
      </Stack>
    </Box>
  );
};

export default TutorPublicVisibilityToggle;
